import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { login as apiLogin, requestOtp as apiRequestOtp, verifyOtp as apiVerifyOtp } from '../services/api';

interface User {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  role: string;
  telephone?: string;
  partenaire_id?: number;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginOtp: (email: string) => Promise<string>;
  verifyOtp: (email: string, otp_code: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('forgest_token');
    const savedUser = localStorage.getItem('forgest_user');
    if (savedToken && savedUser) {
      setToken(savedToken);
      try {
        setUser(JSON.parse(savedUser));
      } catch {
        localStorage.removeItem('forgest_token');
        localStorage.removeItem('forgest_user');
      }
    }
    setLoading(false);
  }, []);

  const saveSession = (tokenValue: string, userValue: User) => {
    localStorage.setItem('forgest_token', tokenValue);
    localStorage.setItem('forgest_user', JSON.stringify(userValue));
    setToken(tokenValue);
    setUser(userValue);
  };

  const login = async (email: string, password: string) => {
    const res = await apiLogin(email, password);
    const { token: t, user: u } = res.data;
    saveSession(t, u);
  };

  const loginOtp = async (email: string): Promise<string> => {
    const res = await apiRequestOtp(email);
    let msg = res.data.message || 'Code OTP envoye par SMS';
    // If SMS failed, show fallback OTP code (dev mode only)
    if (res.data.otp && !res.data.smsSent) {
      msg += ` (Code: ${res.data.otp})`;
    }
    return msg;
  };

  const verifyOtp = async (email: string, otp_code: string) => {
    const res = await apiVerifyOtp(email, otp_code);
    const { token: t, user: u } = res.data;
    saveSession(t, u);
  };

  const logout = () => {
    localStorage.removeItem('forgest_token');
    localStorage.removeItem('forgest_user');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token && !!user,
        loading,
        login,
        loginOtp,
        verifyOtp,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
